import { getItemFromStore, setItemToStore } from "@helpers/utils";
import { createContext, useLayoutEffect, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import ComponentErrorBoundary from "@components/ui-components/error-boundary";
import NavbarComponent from "@layouts/components/navbar";
import SidebarComponent from "@layouts/components/menu";
import Topbar from "@layouts/components/topbar";
import axios from "axios";
import classnames from "classnames";
import { confirmAlert } from "@helpers/toast";
import useNavigation from "../../../navigation";
import useWindowSize from "@hooks/use-window-size";

// Exposes the already-computed sidebar layout state (desktop vs mobile, no-sidebar) to
// descendants rendered through {children} — e.g. LayoutWrapper — so they can pick the
// matching Tailwind margin-left utility without re-deriving state.
export const LayoutMenuContext = createContext({
  isDesktop : true,
  menuHidden: false
});

const Layout = (props) => {
  const { routes, children } = props;

  const [menuHidden, setMenuHidden] = useState(false);
  const { pathname } = useLocation();
  const routeMatch = useMatch(pathname);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    setMenuHidden(
      routes.find((route) => route.path === routeMatch.pathnameBase)?.meta
        .menuHidden
    );
  }, [pathname]);

  const menuData = useNavigation();

  // ** States
  const [menuVisibility, setMenuVisibility] = useState(false);
  const { windowWidth } = useWindowSize();

  // ** Vars
  const location = useLocation();
  const isMenuHidden = menuHidden;
  // One threshold for both: below it the sidebar collapses into the navbar's drawer,
  // above it the sidebar is a permanent column and the navbar gives way to the topbar.
  const isDesktop = windowWidth > 1111;
  const showNavbar = menuHidden ? true : !isDesktop;
  const storedVersion = getItemFromStore("appVersion");
  const [popupOpen, setPopupOpen] = useState(false);

  //** This function will detect the Route Change and will hide the menu on menu item click
  useLayoutEffect(() => {
    if (menuVisibility && windowWidth < 1111) {
      setMenuVisibility(false);
    }
  }, [location]);

  useLayoutEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await axios.get(`/dist/version.txt?${Date.now()}`);
        const currentVersion = new Date(
          response.headers["last-modified"]
        ).valueOf();
        if (!storedVersion) {
          // If no version is stored in localStorage, assume it's the first visit and store the current version
          setItemToStore("appVersion", currentVersion);
        } else if (currentVersion !== storedVersion) {
          // New version detected, show SweetAlert notification
          confirmAlert({
            title            : "New Version Available",
            text             : "A new version of the application is available. Please refresh the page.",
            icon             : "info",
            confirmButtonText: "Refresh",
            showCancelButton : false,
            allowOutsideClick: () => false,
            preConfirm       : () => {
              // Store the current version after the user confirms the notification
              setItemToStore("appVersion", currentVersion);
              // Refresh the page to load the new version
              navigate(0);
            }
          });
          setPopupOpen(true);
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };
    if (!popupOpen) {
      checkVersion();
      document.addEventListener("click", checkVersion);
    }
    return () => {
      document.removeEventListener("click", checkVersion);
    };
  }, [location, popupOpen]);

  return (
    <>
      <div
        className={classnames("relative min-h-screen", {
          // Sidebar is a fixed 240px, always expanded — but only reserve room for it
          // once it's a permanent column rather than an off-canvas drawer.
          "pl-60"      : !isMenuHidden && isDesktop,
          // Reserve space for the fixed top navbar when it's shown
          "pt-[3.7rem]": showNavbar
        })}
        {...(isMenuHidden ? { "data-col": "1-column" } : {})}
      >
        {!isMenuHidden ? (
          <ComponentErrorBoundary name="sidebar" fallbackMessage="Navigation failed to load.">
            <SidebarComponent
              menuData={menuData}
              isDesktop={isDesktop}
              menuVisibility={menuVisibility}
              setMenuVisibility={setMenuVisibility}
            />
          </ComponentErrorBoundary>
        ) : null}
        {showNavbar ? (
          <nav
            className={classnames(
              "flex items-center fixed top-0 inset-x-0 z-[1000] border-b h-[3.7rem] bg-white px-4"
            )}
          >
            <div className="flex items-center w-full">
              <ComponentErrorBoundary name="navbar" fallbackMessage="Top bar failed to load.">
                <NavbarComponent
                  setMenuVisibility={setMenuVisibility}
                  menuHidden={isMenuHidden}
                  menuData={menuData}
                />
              </ComponentErrorBoundary>
            </div>
          </nav>
        ) : null}
        {!isMenuHidden && !showNavbar ? (
          <ComponentErrorBoundary name="topbar" fallbackMessage="Top bar failed to load.">
            <Topbar menuData={menuData} />
          </ComponentErrorBoundary>
        ) : null}
        <LayoutMenuContext.Provider
          value={{
            isDesktop,
            menuHidden: isMenuHidden
          }}
        >
          <ComponentErrorBoundary name="page content">
            {children}
          </ComponentErrorBoundary>
        </LayoutMenuContext.Provider>
      </div>
    </>
  );
};

export default Layout;
